/**
 * Number and date formatting for the super-admin pages, in the active language.
 * One place so a count reads the same on every page.
 */
import i18n from 'i18next';

const locale = () => {
  const lng = (i18n.language || 'en').slice(0, 2);
  return ['en', 'fr', 'es', 'ar'].includes(lng) ? lng : 'en';
};

/** 12 345 / 12,345 — grouped the way the language groups. */
export function formatNumber(value: number | null | undefined, maximumFractionDigits = 0): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale(), { maximumFractionDigits }).format(value);
}

/** 12.3k / 1.2M for tight spaces (tiles, axes). */
export function formatCompact(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(locale(), { notation: 'compact', maximumFractionDigits: 1 }).format(value);
}

export function formatCurrency(value: number | null | undefined, currency = 'usd', maximumFractionDigits = 2): string {
  if (value == null || Number.isNaN(value)) return '—';
  try {
    return new Intl.NumberFormat(locale(), { style: 'currency', currency: currency.toUpperCase(), maximumFractionDigits }).format(value);
  } catch {
    return `${formatNumber(value, maximumFractionDigits)} ${currency.toUpperCase()}`;
  }
}

/**
 * Change from `previous` to `current`, in percent. `null` when there is no
 * baseline to compare with (a percentage from zero means nothing).
 */
export function percentChange(current: number, previous: number): number | null {
  if (!previous) return null;
  return ((current - previous) / previous) * 100;
}

/** "3 Sep 2026" in the active language. */
export function formatDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', year: 'numeric' }).format(d);
}

/** "3 Sep 2026, 14:05" in the active language. */
export function formatDateTime(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat(locale(), { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d);
}
